// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.0.4
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import Layout from './components/Layout'
import Login from './pages/Login'
import RequestTrial from './pages/RequestTrial'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import VerifyEmail from './pages/VerifyEmail'
import Dashboard from './pages/Dashboard'
import Billing from './pages/Billing'
import Licenses from './pages/Licenses'
import Downloads from './pages/Downloads'
import Support from './pages/Support'
import TicketDetail from './pages/TicketDetail'
import Account from './pages/Account'
import MSPDashboard from './pages/MSPDashboard'
import MSPCustomerDetail from './pages/MSPCustomerDetail'
import MSPSupport from './pages/MSPSupport'
import MSPTicketDetail from './pages/MSPTicketDetail'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function IndexRedirect() {
  const { user } = useAuth()
  return <Navigate to={user?.role === 'msp' ? '/msp' : '/dashboard'} replace />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      {/* Public self-serve signup retired — old /register links land on the trial funnel */}
      <Route path="/register" element={<Navigate to="/trial" replace />} />
      <Route path="/trial" element={<PublicRoute><RequestTrial /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password/:token" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<IndexRedirect />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="billing" element={<Billing />} />
        <Route path="licenses" element={<Licenses />} />
        <Route path="downloads" element={<Downloads />} />
        <Route path="support" element={<Support />} />
        <Route path="support/:ticketId" element={<TicketDetail />} />
        <Route path="account" element={<Account />} />
        {/* MSP console — a portal user with role='msp' lands here */}
        <Route path="msp" element={<MSPDashboard />} />
        <Route path="msp/customers/:tenantId" element={<MSPCustomerDetail />} />
        <Route path="msp/support" element={<MSPSupport />} />
        <Route path="msp/support/:ticketId" element={<MSPTicketDetail />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
